import * as yup from 'yup';

export const MediaSchema = yup.object({
  filename: yup.string().optional(),
  public_url: yup.string().optional(),
  bucket: yup.string().optional(),
  path: yup.string().optional(),
  size: yup.number().min(0).optional(),
  mime_type: yup.string().optional(),
  uploaded_at: yup.date().optional(),
});

export const ProcessCheckSchema = yup.object({
  time_of_check: yup.date().required('Time of check is required'),
  was_coffee_turned: yup.boolean().required('Was coffee turned?is required'),
  ph_value: yup
    .number()
    .min(0, 'Ph value must be greater than 0')
    .max(14, 'Ph value must be less than 14')
    .required('Ph value is required'),
  temperature: yup
    .number()
    .required('Temperature is required')
    .test('decimal-places', 'Temperature must have at most 2 decimal places', function (value) {
      if (value === null || value === undefined) return true;
      // Get the original value from the form context to check the raw input
      const originalValue = this.parent?.output_weight_kg;
      if (typeof originalValue === 'string') {
        const decimalPlaces = (originalValue.split('.')[1] || '').length;
        return decimalPlaces <= 2;
      }
      // Fallback: check the number value
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      return decimalPlaces <= 2;
    }),
  notes: yup.string().optional(),
  media: yup.array().of(MediaSchema).optional().nullable(),
});

export const CreateLotProcessSchema = yup
  .object({
    type_of_processing: yup.string().required('Type of processing is required'),
    processing_steps: yup
      .array()
      .of(
        yup.object({
          sequence: yup.number().required('Sequence is required'),
          name: yup.string().required('Name is required'),
          status: yup.string().required('Status is required'),
          data: yup.object().nullable(),
        })
      )
      .required('Processing steps are required'),
  })
  .noUnknown(true);

const BaseDataSchema = yup.object({
  start_at: yup.date().nullable(),
  end_at: yup.date().nullable(),

  input_weight_kg: yup.number().nullable(),
  output_weight_kg: yup.number().nullable(),
  weight_of_skin_kg: yup.number().nullable(),

  // fermentation related
  aerobic: yup.boolean().nullable(),
  modality: yup.string().nullable(),
  agent_type: yup.string().nullable(),
  agent_specific: yup.string().nullable(),
  notes: yup.string().nullable(),
  recipe: yup.string().nullable(),
  process_checks: yup.array().of(ProcessCheckSchema).nullable(),
  media: yup.array().of(MediaSchema).optional().nullable(),
});

const FermentationCompletedSchema = BaseDataSchema.shape({
  start_at: yup.date().required('Start at is required'),
  // end_at: yup.date().required('End at is required'),

  aerobic: yup.boolean().required('Aerobic is required'),
  modality: yup.string().required('Modality is required'),
  agent_type: yup.string().required('Agent type is required'),
  agent_specific: yup.string().required('Agent specific is required'),
  notes: yup.string().required('Notes is required'),
  recipe: yup.string().required('Recipe is required'),
  // process_checks: yup.array().of(ProcessCheckSchema).required('Process checks are required'),

  // optional for fermentation
  end_at: yup.date().nullable(),
  input_weight_kg: yup.number().nullable(),
  output_weight_kg: yup.number().nullable(),
  weight_of_skin_kg: yup.number().nullable(),
});

const NonFermentationCompletedSchema = BaseDataSchema.shape({
  start_at: yup.date().required('Start at is required'),
  end_at: yup.date().required('End at is required'),

  input_weight_kg: yup
    .number()
    .required('Input weight is required')
    .test('decimal-places', 'Input weight must have at most 2 decimal places', function (value) {
      if (value === null || value === undefined) return true;
      // Get the original value from the form context to check the raw input
      const originalValue = this.parent?.output_weight_kg;
      if (typeof originalValue === 'string') {
        const decimalPlaces = (originalValue.split('.')[1] || '').length;
        return decimalPlaces <= 2;
      }
      // Fallback: check the number value
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      return decimalPlaces <= 2;
    }),
  output_weight_kg: yup
    .number()
    .required('Output weight is required')
    .test('decimal-places', 'Output weight must have at most 2 decimal places', function (value) {
      if (value === null || value === undefined) return true;
      // Get the original value from the form context to check the raw input
      const originalValue = this.parent?.output_weight_kg;
      if (typeof originalValue === 'string') {
        const decimalPlaces = (originalValue.split('.')[1] || '').length;
        return decimalPlaces <= 2;
      }
      // Fallback: check the number value
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      return decimalPlaces <= 2;
    }),
  weight_of_skin_kg: yup
    .number()
    .required('Weight of skin is required')
    .test('decimal-places', 'Weight of skin must have at most 2 decimal places', function (value) {
      if (value === null || value === undefined) return true;
      // Get the original value from the form context to check the raw input
      const originalValue = this.parent?.output_weight_kg;
      if (typeof originalValue === 'string') {
        const decimalPlaces = (originalValue.split('.')[1] || '').length;
        return decimalPlaces <= 2;
      }
      // Fallback: check the number value
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      return decimalPlaces <= 2;
    }),
});

const WashingCompletedSchema = BaseDataSchema.shape({
  start_at: yup.date().required('Start at is required'),
  end_at: yup.date().required('End at is required'),

  input_weight_kg: yup
    .number()
    .required('Input weight is required')
    .test('decimal-places', 'Input weight must have at most 2 decimal places', function (value) {
      if (value === null || value === undefined) return true;
      // Get the original value from the form context to check the raw input
      const originalValue = this.parent?.output_weight_kg;
      if (typeof originalValue === 'string') {
        const decimalPlaces = (originalValue.split('.')[1] || '').length;
        return decimalPlaces <= 2;
      }
      // Fallback: check the number value
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      return decimalPlaces <= 2;
    }),
  output_weight_kg: yup
    .number()
    .required('Output weight is required')
    .test('decimal-places', 'Output weight must have at most 2 decimal places', function (value) {
      if (value === null || value === undefined) return true;
      // Get the original value from the form context to check the raw input
      const originalValue = this.parent?.output_weight_kg;
      if (typeof originalValue === 'string') {
        const decimalPlaces = (originalValue.split('.')[1] || '').length;
        return decimalPlaces <= 2;
      }
      // Fallback: check the number value
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      return decimalPlaces <= 2;
    }),
});

const ConditionalDataSchema = yup.lazy((value, ctx) => {
  const { status, name } = ctx.parent;

  // Active / Yet to start → no strict validation
  if (status !== 'Completed' && status !== 'In Progress') {
    return BaseDataSchema.nullable();
  }

  // Completed + Fermentation
  if (name === 'Fermentation') {
    return FermentationCompletedSchema.required('Data is required');
  }

  if (name === 'Washing') {
    return WashingCompletedSchema.required('Data is required');
  }

  // Completed + Other steps
  return NonFermentationCompletedSchema.required('Data is required');
});

export const ProcessingStepSchema = yup
  .object({
    sequence: yup.number().required('Sequence is required'),
    name: yup.string().required('Name is required'),
    status: yup
      .string()
      // .enums(['Completed', 'In Progress', 'Yet to start', 'In Progress'])
      .required('Status is required'),
    data: ConditionalDataSchema,
  })
  .noUnknown(true);

export const EndFermentationSchema = yup.object({
  end_at: yup.date().required('End at is required'),
  output_weight_kg: yup.number().required('Output weight is required'),
});
